import React, { useEffect, useState } from "react";
import { makeGetCall } from "../../effects/requests";
import { BOOKING_STATUS } from "../../types";

import endpoints from "../../utils/endpoints";
import { STRING_CONSTANTS } from "../../utils/constants/stringConstants";
import { getURLstylistCode } from "../../utils/globalFunctions";
import useStore from "../../zustandstore";
import Cancellation from "../Emails/Cancellation";
import Confirmed from "../Emails/Confirmed";
import NoBookingFound from "./NoBookingFound";

/**
 * Normalize online_status from the API (number or string) for comparisons.
 */
function getOnlineStatus(booking: any) {
  if (booking?.online_status === null || booking?.online_status === undefined) {
    return null;
  }

  const status = Number(booking.online_status);

  return Number.isNaN(status) ? null : status;
}

/**
 * Extract booking payload from a standard API envelope.
 */
function getBookingFromResponse(apiResponse: any) {
  if (!apiResponse) {
    return null;
  }

  if (apiResponse?.data?.id) {
    return apiResponse.data;
  }

  if (apiResponse?.id) {
    return apiResponse;
  }

  return null;
}

/**
 * Whether the booking should show the confirmed / upcoming details screen.
 */
function shouldShowConfirmedView(booking: any) {
  if (!booking?.id) {
    return false;
  }

  const status = getOnlineStatus(booking);

  if (status === null) {
    return true;
  }

  return (
    status === BOOKING_STATUS.CONFIRMED ||
    status === BOOKING_STATUS.PENDING ||
    status === BOOKING_STATUS.UPDATED ||
    status === BOOKING_STATUS.CREATED
  );
}

/**
 * Whether the booking should show the cancelled details screen.
 */
function shouldShowCancelledView(booking: any) {
  if (!booking?.id) {
    return false;
  }

  const status = getOnlineStatus(booking);

  return (
    status === BOOKING_STATUS.CANCELLED ||
    status === BOOKING_STATUS.REJECTED ||
    booking.is_deleted === true
  );
}

export default function ViewDetails() {
  const [bookingDetail, setBookingDetail] = useState<any>();
  const [noBooking, setNoBooking] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { setBookingId, stylistUniqueCode } = useStore((state: any) => state);

  async function getResponse() {
    const urlParams = new URLSearchParams(window.location.search);
    const bookingId = urlParams.get("id");
    const stylistCode = getURLstylistCode();

    setBookingId(bookingId);
    setIsLoading(true);
    setNoBooking(false);

    if (!bookingId || !stylistCode) {
      setNoBooking(true);
      setIsLoading(false);
      return;
    }

    try {
      const apiResponse = await makeGetCall({
        url: `${endpoints.booking}${bookingId}/`,
        params: { stylist_unique_code: stylistCode },
      });

      const booking = getBookingFromResponse(apiResponse);
      const statusCode = apiResponse?.status?.code;

      if (booking) {
        setBookingDetail(booking);
        return;
      }

      if (statusCode && statusCode !== 200) {
        setBookingDetail(undefined);
        setNoBooking(true);
      }
    } catch (err) {
      console.log(err);

      try {
        const listResponse: any = await makeGetCall({
          url: endpoints.customer_booking_list(stylistCode),
          params: { status: "upcoming" },
        });
        const upcoming = listResponse?.data?.results || [];
        const fromList = upcoming.find((item: any) => item.id === bookingId);

        if (fromList) {
          const detailResponse = await makeGetCall({
            url: `${endpoints.booking}${bookingId}/`,
            params: { stylist_unique_code: stylistCode },
          });
          const booking = getBookingFromResponse(detailResponse);

          if (booking) {
            setBookingDetail(booking);
            return;
          }

          setBookingDetail(fromList);
          return;
        }

        const pastResponse: any = await makeGetCall({
          url: endpoints.customer_booking_list(stylistCode),
          params: { status: "past" },
        });
        const past = pastResponse?.data?.results || [];
        const fromPast = past.find((item: any) => item.id === bookingId);

        if (fromPast) {
          setBookingDetail(fromPast);
          return;
        }
      } catch (listErr) {
        console.log(listErr);
      }

      setBookingDetail(undefined);
      setNoBooking(true);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getResponse();
  }, []);

  const showConfirmed = shouldShowConfirmedView(bookingDetail);
  const showCancelled = shouldShowCancelledView(bookingDetail);

  return (
    <div className="booking-wrapper">
      {!isLoading && showConfirmed && (
        <div className="order-status confirmed">
          <Confirmed
            bookingDetail={bookingDetail}
            setBookingId={setBookingId}
            stylistUniqueCode={stylistUniqueCode}
          />
        </div>
      )}

      {!isLoading && showCancelled && (
        <div className="order-status confirmed cancelled">
          <Cancellation
            bookingDetail={bookingDetail}
            setBookingId={setBookingId}
            stylistUniqueCode={stylistUniqueCode}
          />
        </div>
      )}

      {!isLoading && noBooking && (
        <NoBookingFound message={STRING_CONSTANTS.booking_details_not_found} />
      )}
    </div>
  );
}
